// Time range definitions for the analytics dashboard.
//
// Each preset returns both the current range and an "equivalent prior period"
// range used for delta comparisons:
//   - This Week / Last Week  → same-length week immediately preceding
//   - MTD / QTD / YTD        → same offset (1st-through-Nth-day) of prior month/quarter/year
//   - Last Month             → the month before the selected month
//   - Custom                 → same length window immediately preceding `start`
//
// Days are stored as `yyyy-MM-dd` strings matching the keys used in the
// `salesMetrics/{YYYY-MM-DD}` collection. Local-time keys are used because
// that is how dashboard users think about "today"; this matches the date-range
// behavior in pages/Reports.js. There is a small skew at the day boundary if
// salesMetrics aggregation happens to land near midnight UTC, but for a small-
// business dashboard this is acceptable and simpler than mixing zones.
import {
  format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  startOfQuarter, startOfYear, subWeeks, subMonths, subYears, subDays,
  addDays, differenceInCalendarDays, isAfter, isValid,
} from 'date-fns';

export const RANGE_OPTIONS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'mtd', label: 'MTD' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'qtd', label: 'QTD' },
  { value: 'ytd', label: 'YTD' },
  { value: 'custom', label: 'Custom' },
];

const dayKey = (d) => format(d, 'yyyy-MM-dd');

// Cap a window's end at "today" so a partial-period range (e.g. This Week,
// MTD) doesn't include future days that can't have any salesMetrics docs yet.
const capAtToday = (end, today) => (isAfter(end, today) ? today : end);

// Return { current: { start, end, startKey, endKey, label },
//          prior:   { start, end, startKey, endKey, label } }
// for the selected preset. `customStart`/`customEnd` are ISO date strings.
export function resolveRange(preset, { customStart, customEnd, today = new Date() } = {}) {
  // Normalize "today" to start-of-day so endOf*/dayKey behave consistently.
  const now = today;

  let curStart, curEnd, priorStart, priorEnd, label;

  switch (preset) {
    case 'this_week': {
      curStart = startOfWeek(now);
      curEnd = capAtToday(endOfWeek(now), now);
      priorStart = startOfWeek(subWeeks(now, 1));
      // Prior period: same days last week (same length window).
      const len = differenceInCalendarDays(curEnd, curStart);
      priorEnd = addDays(priorStart, len);
      label = 'This Week';
      break;
    }
    case 'last_week': {
      curStart = startOfWeek(subWeeks(now, 1));
      curEnd = endOfWeek(subWeeks(now, 1));
      priorStart = startOfWeek(subWeeks(now, 2));
      priorEnd = endOfWeek(subWeeks(now, 2));
      label = 'Last Week';
      break;
    }
    case 'mtd': {
      curStart = startOfMonth(now);
      curEnd = now;
      // Prior MTD: same number of days into the previous month.
      const len = differenceInCalendarDays(curEnd, curStart);
      priorStart = startOfMonth(subMonths(now, 1));
      priorEnd = addDays(priorStart, len);
      label = 'MTD';
      break;
    }
    case 'last_month': {
      curStart = startOfMonth(subMonths(now, 1));
      curEnd = endOfMonth(subMonths(now, 1));
      priorStart = startOfMonth(subMonths(now, 2));
      priorEnd = endOfMonth(subMonths(now, 2));
      label = 'Last Month';
      break;
    }
    case 'qtd': {
      curStart = startOfQuarter(now);
      curEnd = now;
      const len = differenceInCalendarDays(curEnd, curStart);
      priorStart = startOfQuarter(subMonths(now, 3));
      priorEnd = addDays(priorStart, len);
      label = 'QTD';
      break;
    }
    case 'ytd': {
      curStart = startOfYear(now);
      curEnd = now;
      const len = differenceInCalendarDays(curEnd, curStart);
      priorStart = startOfYear(subYears(now, 1));
      priorEnd = addDays(priorStart, len);
      label = 'YTD';
      break;
    }
    case 'custom': {
      const parsedStart = customStart ? parseISO(customStart) : null;
      const parsedEnd = customEnd ? parseISO(customEnd) : null;
      // Fall back to MTD if either endpoint is missing/invalid.
      if (!parsedStart || !parsedEnd || !isValid(parsedStart) || !isValid(parsedEnd) || isAfter(parsedStart, parsedEnd)) {
        return resolveRange('mtd', { today });
      }
      curStart = parsedStart;
      curEnd = parsedEnd;
      const len = differenceInCalendarDays(curEnd, curStart);
      priorEnd = subDays(curStart, 1);
      priorStart = subDays(priorEnd, len);
      label = 'Custom';
      break;
    }
    default:
      return resolveRange('mtd', { today });
  }

  return {
    current: {
      start: curStart,
      end: curEnd,
      startKey: dayKey(curStart),
      endKey: dayKey(curEnd),
      label,
    },
    prior: {
      start: priorStart,
      end: priorEnd,
      startKey: dayKey(priorStart),
      endKey: dayKey(priorEnd),
      label: `Prior ${label}`,
    },
  };
}

// Sum a list of `salesMetrics` docs into a single totals object covering the
// fields the dashboard cares about. Docs whose `date` falls outside [startKey,
// endKey] are skipped, which lets the caller pass a single fetched collection
// and reuse it for both current and prior windows.
export function sumMetricsInRange(metricsDocs, startKey, endKey) {
  const totals = {
    newLeads: 0,
    quotesSent: 0,
    quotesSentValue: 0,
    won: 0,
    wonValue: 0,
    lost: 0,
    days: 0,
  };
  if (!Array.isArray(metricsDocs) || !startKey || !endKey) return totals;
  for (const m of metricsDocs) {
    const d = m?.date || m?.id;
    if (!d || d < startKey || d > endKey) continue;
    totals.newLeads += Number(m.newLeads) || 0;
    totals.quotesSent += Number(m.quotesSent) || 0;
    totals.quotesSentValue += Number(m.quotesSentValue) || 0;
    totals.won += Number(m.won) || 0;
    totals.wonValue += Number(m.wonValue) || 0;
    totals.lost += Number(m.lost) || 0;
    totals.days += 1;
  }
  totals.quotesSentValue = Math.round(totals.quotesSentValue * 100) / 100;
  totals.wonValue = Math.round(totals.wonValue * 100) / 100;
  return totals;
}

// % delta of current vs prior. Returns:
//   { value: number|null, direction: 'up'|'down'|'flat'|'none' }
// `value` is null when prior is 0 (no baseline to compare against), so the
// dashboard can show "—" instead of an arbitrary 100% / Infinity figure.
export function pctDelta(current, prior) {
  const c = Number(current) || 0;
  const p = Number(prior) || 0;
  if (p === 0) {
    if (c === 0) return { value: 0, direction: 'flat' };
    return { value: null, direction: 'none' };
  }
  const pct = ((c - p) / p) * 100;
  const direction = pct > 0.05 ? 'up' : pct < -0.05 ? 'down' : 'flat';
  return { value: pct, direction };
}
